'use client';

import useSWR from 'swr';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';

import { paths } from 'src/routes/paths';
import { NyttFärgTema, ChipFärger } from 'src/theme/NyttFärgtema';
import { hämtaEllerSkapaBokslutProcess, hämtaÅrsredovisningsunderlag } from 'src/sections/bokforing/bokslut/BokslutProcessService';
import { useGetAktieutdelningFörslag } from 'src/actions/aktieutdelning';
import type { IBokslutProcess } from 'src/types/nya/ekonomi/bokforing/bokslutTyper';
import { useAppContext } from '@/src/context/app';

import type { KortProps } from '../kort-katalog';
import DomainCard from '../components/domain-card';
import { NyckeltalRad, formatKr, formatSignedKr, toDayjsSafe, swrOptions } from './kort-primitiver';

// ----------------------------------------------------------------------

export default function BokslutKort({ orgId, räkenskapsår }: KortProps) {
    const { organisation } = useAppContext();

    const start = räkenskapsår?.förstaDag;
    const slut = räkenskapsår?.sistaDag;
    const startKey = start ? String((start as any).format?.('YYYY-MM-DD') ?? start) : '';
    const slutKey = slut ? String((slut as any).format?.('YYYY-MM-DD') ?? slut) : '';

    const { data: process, isLoading } = useSWR<IBokslutProcess>(
        orgId && räkenskapsår ? ['bokslut-process', orgId, startKey, slutKey] : null,
        () => hämtaEllerSkapaBokslutProcess(orgId, räkenskapsår!),
        swrOptions
    );

    const bokslutStart = toDayjsSafe(räkenskapsår?.förstaDag)?.format('YYYY-MM-DD');
    const bokslutSlut = toDayjsSafe(räkenskapsår?.sistaDag)?.format('YYYY-MM-DD');

    const { förslag } = useGetAktieutdelningFörslag(
        orgId,
        bokslutStart,
        bokslutSlut,
        null
    );

    const { data: beslutadAktieutdelning } = useSWR<number | undefined>(
        orgId && bokslutStart && bokslutSlut
            ? ['arsredovisning-underlag-utdelning', orgId, bokslutStart, bokslutSlut]
            : null,
        () => hämtaÅrsredovisningsunderlag(orgId, bokslutStart!, bokslutSlut!).then((u) => u?.aktieutdelning),
        swrOptions
    );

    const stegVärden = process?.stegStatus ? Object.values(process.stegStatus) : [];
    const totaltSteg = stegVärden.length > 0 ? stegVärden.length : 6;
    const klaraSteg = stegVärden.filter((s) => s === 'KLAR').length;

    const årLabel = (() => {
        if (!räkenskapsår) return undefined;
        const s = räkenskapsår.förstaDag as any;
        const startDate = s?.format ? s.format('YYYY-MM-DD') : String(s);
        return startDate?.substring(0, 4);
    })();

    if (isLoading) {
        return (
            <DomainCard
                title="Bokslut"
                icon="solar:file-text-bold"
                theme={NyttFärgTema.Kombinationer[7]}
                href={paths.dashboard.ekonomi.bokforing.bokslut.root}
                primaryMetric={{ value: '—', label: 'steg klara' }}
            >
                <Skeleton variant="rectangular" height={8} sx={{ borderRadius: 999 }} />
            </DomainCard>
        );
    }

    if (!process) {
        return (
            <DomainCard
                title="Bokslut"
                icon="solar:file-text-bold"
                theme={NyttFärgTema.Kombinationer[7]}
                href={paths.dashboard.ekonomi.bokforing.bokslut.root}
                emptyMessage="Inget räkenskapsår registrerat för bokslutsprocessen ännu."
            />
        );
    }

    const status = process.slutförd ? 'SLUTFÖRT' : klaraSteg > 0 ? 'PÅGÅR' : 'EJ PÅBÖRJAT';

    return (
        <DomainCard
            title="Bokslut"
            icon="solar:document-bold"
            theme={NyttFärgTema.Kombinationer[7]}
            href={paths.dashboard.ekonomi.bokforing.bokslut.root}
            primaryMetric={{ value: `${klaraSteg} / ${totaltSteg}`, label: 'steg klara' }}
            secondaryMetric={årLabel ? { value: årLabel, label: 'räkenskapsår' } : undefined}
            statusChip={{ label: status, color: ChipFärger[7] }}
        >
            {förslag ? (
                <Stack
                    divider={<Box sx={(theme) => ({ borderTop: `1px dashed ${theme.palette.divider}` })} />}
                    spacing={0}
                >
                    <NyckeltalRad etikett="Årets resultat" belopp={formatSignedKr(förslag.åretsResultatFöreSkatt)} />
                    <NyckeltalRad etikett="Skatt" belopp={formatSignedKr(-Math.abs(förslag.bolagsskatt))} />
                    <NyckeltalRad
                        etikett="Årets resultat efter skatt"
                        belopp={formatSignedKr(förslag.åretsResultatEfterSkatt)}
                        framhävd
                    />
                    <NyckeltalRad
                        etikett="Aktieutdelning"
                        belopp={formatKr(beslutadAktieutdelning ?? förslag.föreslagenAktieutdelning)}
                    />
                </Stack>
            ) : (
                <Stack spacing={0.75}>
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" height={20} />
                </Stack>
            )}
        </DomainCard>
    );
}
